import { useTranslation } from 'react-i18next'

import { Flex, FormLabel } from '@chakra-ui/react'

import {
  Box as DarkBox,
  Button as DarkButton,
  Flex as DarkFlex,
} from '@hivewatch/dark'

import { Button, FORM_LABEL_STYLES } from '@/components/ui'

interface FacilitySelectHeaderIProps {
  allFacilitiesSelected: boolean
  onToggleSelectAll: () => void
  isDisabled?: boolean
  dataTestId?: string
  /**
   * Rebrand: keys the label to the theme and matches the design system's
   * uppercase field label. Omit to keep the legacy appearance untouched.
   */
  colorScheme?: 'dark' | 'light'
}

export const FacilitySelectHeader = ({
  allFacilitiesSelected,
  onToggleSelectAll,
  isDisabled = false,
  dataTestId,
  colorScheme,
}: FacilitySelectHeaderIProps) => {
  const { t } = useTranslation()
  const toggleButtonLabel = allFacilitiesSelected
    ? t('sops:crud_deselect_all')
    : t('sops:crud_select_all')
  const toggleButton = (
    <Button
      _active={{ color: 'brand.200' }}
      _focus={{ outline: 'none' }}
      _hover={{ textDecoration: 'none' }}
      color='brand.200'
      data-testid={dataTestId}
      fontSize='14px'
      height='auto'
      isDisabled={isDisabled}
      minW='auto'
      onClick={onToggleSelectAll}
      p='0'
      textDecoration='none'
      variant='link'
    >
      {toggleButtonLabel}
    </Button>
  )

  if (colorScheme) {
    const labelColor =
      colorScheme === 'light' ? 'light.text.primary' : 'text.primary'

    return (
      <DarkFlex alignItems='flex-start' justifyContent='space-between' mb='5px'>
        <FormLabel htmlFor='facilities' m='0'>
          <DarkBox as='span' color={labelColor} pb='4px' textStyle='label'>
            {t('sops:crud_facilities_label')}
          </DarkBox>
        </FormLabel>
        <DarkButton
          colorScheme={colorScheme}
          data-testid={dataTestId}
          disabled={isDisabled}
          onClick={onToggleSelectAll}
          size='sm'
          variant='tertiary'
        >
          {toggleButtonLabel}
        </DarkButton>
      </DarkFlex>
    )
  }

  return (
    <Flex alignItems='center' justifyContent='space-between' mb='8px'>
      <FormLabel
        color={FORM_LABEL_STYLES.color}
        fontSize={FORM_LABEL_STYLES.fontSize}
        htmlFor='facilities'
        letterSpacing={FORM_LABEL_STYLES.letterSpacing}
        m='0'
        opacity={FORM_LABEL_STYLES.opacity}
      >
        {t('sops:crud_facilities_label')}
      </FormLabel>
      {toggleButton}
    </Flex>
  )
}
